/**
 * How a trust's Master registration reference is PRINTED.
 *
 * ── WHY THIS IS A SHARED DICTIONARY AND NOT A TERNARY ───────────────────────
 *
 * A frame of the 25 August walkthrough shows, side by side on the trust
 * overview:
 *
 *     TRUST REGISTRATION NO   Pending registration
 *     REGISTRATION DATE       29 Jan 2013
 *
 * Both cannot be true. That was fixed on 26 August, on the trust overview, and
 * the "My trusts" card two clicks away kept printing the raw placeholder the
 * whole time. Same fact, two render paths, one of them fixed. That is the exact
 * shape of the bug that made "Settlor" survive four asks, so the mapping lives
 * in one place and both screens read it.
 *
 * ── WHAT EACH ANSWER IS ALLOWED TO CLAIM ─────────────────────────────────────
 *
 * Beno's standing rule (2026-08-01): a fact not in the documents must not show.
 * The corollary bit twice here, in opposite directions:
 *
 *   - "Pending registration" for a trust with a Master's letter on file said the
 *     trust is not registered when the documents prove it is.
 *   - "Not yet registered" (the 26 August replacement) said the same false thing
 *     about a genuinely registered trust whose Letter of Authority simply had
 *     not been uploaded yet.
 *
 * Nothing on file is not evidence of absence. The honest answer for an absent
 * document is that nothing is on file, which invites the question; a wrong value
 * does not. So the third case reports the state of the FILE and makes no claim
 * about the Master's register at all.
 */

/** Mirrors the backend's placeholder literal for a trust whose Master
 *  registration / IT number has not been recognised yet
 *  (`backend/src/lib/ingestion/confirm-core.ts`,
 *  `backend/src/lib/clients/create-one-client.ts`). Never rendered. */
export const PENDING_REGISTRATION = 'Pending registration';

export interface RegistrationDisplay {
  /** What to print. */
  text: string;
  /** True only when `text` is an actual Master reference, so a caller can set
   *  it in mono/code and leave the prose answers as prose. */
  isNumber: boolean;
}

/**
 * @param registration     `trust.registration` as the API returns it, which is
 *                         the real reference when one has been read and the
 *                         backend placeholder when none has.
 * @param registrationDate `trust.registrationDate` (ISO) off the Master's
 *                         Letter of Authority, absent when no letter is on file.
 *                         The Master does not date a letter for a trust it has
 *                         not registered, so a date here is documentary proof of
 *                         registration even when the number itself is unread.
 */
export function registrationDisplay(
  registration: string | null | undefined,
  registrationDate: string | null | undefined,
): RegistrationDisplay {
  if (registration && registration !== PENDING_REGISTRATION) {
    return { text: registration, isNumber: true };
  }
  if (registrationDate) {
    return { text: 'Registered, number not yet read', isNumber: false };
  }
  return { text: 'No registration number on file', isNumber: false };
}
